import React from "react";
import Button from "components/Button";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import Badge from "components/Badge";
import { observer } from "mobx-react-lite";
import { IconHelpCircle } from "@tabler/icons-react";
import { globalStore } from "store/GlobalStore";
import ConfirmModal from "components/ConfirmModal";

export default observer(function FullScreenLayout(props: {
  title: string;
  helpURL: string;
  children: React.ReactNode;
  buildingName?: string;
  closeButtonTitle?: string;
  closeButtonAction?: () => void;
  confirmTitle?: string;
  confirmMessage?: string;
  confirmButtontitle?: string;
  showConfirm?: () => boolean;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  React.useEffect(() => {
    globalStore.setSidebarOpened(false);
  }, []);

  function onClose(confirmed?: boolean) {
    const hasError = props.showConfirm?.();
    if (hasError && !confirmed) {
      setConfirmOpen(true);
      return;
    }
    location.state
      ? navigate(location.state)
      : navigate("/", { replace: true });
  }

  function buttons(className: string) {
    return (
      <div className={className}>
        <Button
          width="fit-content"
          className="mr-4"
          onClick={() =>
            window.open(props.helpURL, "_blank", "noopener,noreferrer")
          }
          leadingIcon={<IconHelpCircle />}
          type="gray"
        >
          {t("commons.help")}
        </Button>
        <Button width="fit-content" onClick={() => onClose(false)}>
          {props.closeButtonTitle || t("commons.close")}
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-white flex flex-col h-screen fixed top-0 left-0 z-20 w-full">
      <div
        id="editor-header"
        className="sm:flex w-full bg-white px-6 py-3 items-center justify-between border-b border-gray-300"
      >
        <div id="editor-title" className="sm:flex mb-3 sm:mb-0">
          <div className="flex justify-between">
            <div className="text-xl font-semibold text-black mr-4 mb-2 sm:mb-0">
              {props.title}
            </div>
            {buttons("sm:hidden flex")}
          </div>
          {props.buildingName && (
            <Badge type="info" className="mr-4 mb-2 sm:mb-0">
              {props.buildingName}
            </Badge>
          )}
        </div>
        {buttons("sm:flex hidden")}
      </div>
      {props.children}
      <ConfirmModal
        title={props.confirmTitle ?? t("commons.areYouSure")}
        descriptionMesage={props.confirmMessage}
        confirmButtonTitle={props.confirmButtontitle ?? t("commons.understood")}
        open={confirmOpen}
        close={() => setConfirmOpen(false)}
        onConfirm={() => onClose(true)}
        containerClassName="!min-w-[640px]"
      />
    </div>
  );
});
